import { useMemo } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { FundingEventBadge } from '@/shared/components/atoms/FundingEventBadge';
import { Link } from '@/shared/components/atoms/Link';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import ViewEventButton from '@/shared/components/ViewEventButton';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getFundingEventsQuery } from '@/shared/graphql/explorer/account';
import {
  type CastAccountFundingEventFragment,
  accountFundingEventCastFields,
} from '@/shared/graphql/explorer/fragments';
import { type AccountRole } from '@/shared/graphql/generated/explorer/graphql';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import { buildChainflipExplorerLink, getChainflipExplorerTarget } from '@/shared/utils/chainflip';
import FlipAmount from '@/shared/utils/FlipAmount';
import { etherscanUrl } from '@/shared/utils/helpers';

const TransactionCell = ({
  txHash,
  swapRequest,
  accountChannel,
}: CastAccountFundingEventFragment) => {
  if (txHash) {
    return (
      <span className="flex items-center space-x-0.5">
        <div className="flex h-6 w-6 items-center justify-center">
          <ChainLogo chain="Ethereum" size="medium" />
        </div>
        <Link href={etherscanUrl('tx', txHash)} target="_blank" underline noPropagate color="white">
          <span className="whitespace-nowrap">{abbreviate(txHash)}</span>
        </Link>
      </span>
    );
  }

  if (swapRequest) {
    return (
      <Link href={buildChainflipExplorerLink(swapRequest.nativeId, 'swaps')} underline noPropagate>
        Swap #{swapRequest.nativeId}
      </Link>
    );
  }

  if (accountChannel) {
    return (
      <Link
        href={buildChainflipExplorerLink(
          `${accountChannel.issuedBlockId}-${accountChannel.chain}-${accountChannel.channelId}`,
          'channels',
        )}
        underline
        noPropagate
      >
        Channel {accountChannel.issuedBlockId}-{accountChannel.chain}-{accountChannel.channelId}
      </Link>
    );
  }

  return 'N/A';
};

const FundingEventsTableRow = ({
  account,
  accountFundingEvent,
  displayAccountId,
}: {
  account: { idSs58: string; role: AccountRole; alias?: string | null };
  accountFundingEvent: CastAccountFundingEventFragment;
  displayAccountId: boolean;
}) => {
  const { event, amount, type, rebalanceCounterparty } = accountFundingEvent;

  const counterparty = rebalanceCounterparty?.idSs58;

  return (
    <tr>
      {displayAccountId && (
        <td className="text-12">
          <div className="flex flex-col gap-y-2">
            {account.alias}
            <div className="flex items-center gap-x-1">
              <Link
                href={buildChainflipExplorerLink(account.idSs58, 'accounts')!}
                target={getChainflipExplorerTarget()}
                rel="noreferrer"
                underline
                noPropagate
                color="light"
                className="whitespace-nowrap"
              >
                {abbreviate(account.idSs58, 4)}
              </Link>
              <CopyButton textToCopy={account.idSs58} />
            </div>
          </div>
        </td>
      )}
      <td>
        <FundingEventBadge eventType={type} />
      </td>
      <td>
        <FlipLabel amount={amount} />
      </td>
      <td>
        <TransactionCell {...accountFundingEvent} />
      </td>
      <td>
        <div className="flex items-center gap-x-1 whitespace-nowrap">
          {counterparty ? (
            <>
              <ArrowIcon
                className={amount.gt(0) ? 'text-cf-green-1' : 'text-cf-red-1'}
                direction={amount.gt(0) ? 'right' : 'left'}
              />
              <Link
                href={buildChainflipExplorerLink(counterparty, 'accounts')}
                underline
                noPropagate
              >
                {abbreviate(counterparty, 4)}
              </Link>
            </>
          ) : (
            'N/A'
          )}
        </div>
      </td>
      <td className="whitespace-nowrap">
        <ViewEventButton event={event} />
      </td>
      <td className="whitespace-nowrap text-right text-12 text-cf-light-2">
        <AgeWithTooltip timestamp={event.block.timestamp} />
      </td>
    </tr>
  );
};

export const FundingAndRedemptionsTable = ({
  accountIds,
}: {
  accountIds: string[];
}): React.JSX.Element => {
  const {
    data: stakeEventsPaginated,
    isLoading: eventsLoading,
    error,
    paginationControls,
  } = useRemotePagination(getFundingEventsQuery, {
    variables: { idSs58s: accountIds },
    useQueryParam: true,
    totalCount: (data) => data.events?.totalCount ?? 0,
    refetchInterval: TABLE_POLL_INTERVAL,
  });

  const optimizedRows = useMemo(
    () =>
      FlipAmount.castObject(stakeEventsPaginated, accountFundingEventCastFields)?.events?.edges.map(
        (event) => (
          <FundingEventsTableRow
            key={event.node.id}
            accountFundingEvent={event.node}
            account={event.node.account}
            displayAccountId={accountIds.length > 1}
          />
        ),
      ) ?? [],
    [stakeEventsPaginated, accountIds],
  );

  if (error) throw error;
  return (
    <TableV2
      columns={[
        ...(accountIds.length > 1 ? ['Validator / Operator'] : []),
        'Type',
        'Amount',
        'Transaction',
        'Destination or Source',
        'Event',
        { alignment: 'right', name: 'Age' },
      ]}
      rows={optimizedRows}
      isLoading={eventsLoading}
      paginationControls={paginationControls}
    />
  );
};
